/* 
this file is a root phaser file. Consist from 4 default methods(init, preload, create etc)
and custom methods. Custo methods are connectors between therapist render and different tasks.

Althought contains changeble settings.
*/

import Phaser from 'phaser';
import { CalculationTask, CalculationTaskConfiguration } from '../tasks/CalculationTask';
import { AudioTask, AudioTaskConfiguration } from '../tasks/AudioTask';
import { CompositeTask, CompositeTaskConfiguration } from '../tasks/CompositeTask';
import { Zoomer } from '../../../enums/ZoomerSettings';
import CONSTANTS from '../../../CONSTANTS/CONSTANTS';
import { MovingTargetTask, MovingTargetTaskConfiguration } from '../tasks/MovingTargetTask';
import { ReactionTask, ReactionTaskConfiguration } from '../tasks/ReactionTask';
import { StroopTaskConfiguration, StroopTask } from '../tasks/StroopTask';
import { CalculationDifficultyOptions } from '../../../../../commands/practitioner/enums/CalculationDifficultyOptions';
import { TaskBallColor } from '../../../../../commands/practitioner/enums/TaskBallColor';
import { RandomNumberGenerator } from './RandomNumberGenerator';
import backendService from '../../../../../services/BackendService';
// that import needed to solve problem, that u can't call React hook in the class based component
import sessionId from './SessionId';
import ChangeBallSpeed from './ChangeBallSpeed';
import { ITaskConfiguration } from '../tasks/TaskDefinitions';
import Color = Phaser.Display.Color;
import { TaskType } from '../../../../../commands/practitioner/enums/TaskType';
import { State } from '../../../../../commands/practitioner/enums/State';

type Sprite = Phaser.GameObjects.Sprite;

//difficulty times that will increase speed of movement. BaseSpeed = 2000, so 2000 -500 = 1500 E.T.C
let easyDifficulty = CONSTANTS.tascScene.easyDifficulty;
let normalDifficulty = CONSTANTS.tascScene.normalDifficulty;
let hardDifficulty = CONSTANTS.tascScene.hardDifficulty;

export class TaskTheme {
  readonly name: string;
  readonly background: string = 'greybackground';
  readonly spriteKeyPostfix: string = 'ball';
  /** Optional second sprite layer drawn on top of the body and never tinted (e.g. the child penguin's face). */
  readonly overlaySpriteKey?: string;
  // id: typeof IdSession;
  // Maps from colour to sprite key ( red -> red-ball)
  readonly colourNameToSpriteKey: Map<string, string> = new Map<string, string>(TaskTheme.CreateColourMapping('ball'));
  readonly colourNameToColourNumber: Map<string, number> = new Map<string, number>(
    TaskTheme.CreateColourNumberMapping()
  );
  constructor(
    name: string,
    background?: string,
    spriteKeyPostfix?: string,
    isColorMapped?: boolean,
    overlaySpriteKey?: string
  ) {
    this.name = name;
    this.background = background ?? this.background;
    this.spriteKeyPostfix = spriteKeyPostfix ?? this.spriteKeyPostfix;
    this.overlaySpriteKey = overlaySpriteKey;

    if (spriteKeyPostfix)
      this.colourNameToSpriteKey = new Map<string, string>(
        isColorMapped
          ? TaskTheme.CreateColourMapping(spriteKeyPostfix)
          : TaskTheme.CreateStaticColourMapping(spriteKeyPostfix)
      );
  }

  // Returns : [ `Colour`, `Colour`-`key`] (e.g.: blue, blue-ball)
  static CreateColourMapping(key: string): readonly [string, string][] {
    return Object.keys(TaskBallColor)
      .filter((v) => isNaN(Number(v)))
      .map((n) => {
        return [n, n + '-' + key];
      });
  }

  // Returns : [ `Colour`, 'key`] (e.g.: blue, ball)
  static CreateStaticColourMapping(key: string): readonly [string, string][] {
    return Object.keys(TaskBallColor)
      .filter((v) => isNaN(Number(v)))
      .map((n) => {
        return [n, key];
      });
  }

  // Returns : [ `Colour`, #] (e.g.: blue, 0000FF)
  static CreateColourNumberMapping(): readonly [string, number][] {
    let mapping: [string, number][] = [];
    mapping.push([TaskBallColor[TaskBallColor.Silver], Color.GetColor(211, 211, 211)]);
    mapping.push([TaskBallColor[TaskBallColor.Blue], Color.GetColor(50, 135, 240)]);
    mapping.push([TaskBallColor[TaskBallColor.Red], Color.GetColor(255, 120, 100)]);
    mapping.push([TaskBallColor[TaskBallColor.Purple], Color.GetColor(200, 100, 200)]);

    return mapping;
  }
}

export class Difficulty {
  public percentage: number;
  public index: number;
  public configuration: CompositeTaskConfiguration;

  constructor(percentage: number, index: number, configuration: CompositeTaskConfiguration) {
    this.percentage = percentage;
    this.index = index;
    this.configuration = configuration;
  }
}

export class Answer {
  public time: number = 0;
  public isAnswerCorrect: boolean = false;
}

class CommandQueue {
  private queue: { sessionId: string; treatmentId: string; value: boolean }[] = [];
  private isProcessing: boolean = false;

  enqueue(sessionId: string, treatmentId: string, value: boolean) {
    this.queue.push({ sessionId, treatmentId, value });
    this.processQueue();
  }

  private async processQueue() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    while (this.queue.length > 0) {
      const { sessionId, treatmentId, value } = this.queue.shift()!;
      await this.sendCommand(sessionId, treatmentId, value);
      await this.delay(300);
    }

    this.isProcessing = false;
  }

  private async sendCommand(sessionId: string, treatmentId: string, value: boolean): Promise<void> {
    const command = value ? 'RPF:1' : 'RPF:0';
    await backendService.sendReactionResponseCommand(sessionId, treatmentId, value);
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

export default class TaskScene extends Phaser.Scene {
  public themes: Array<TaskTheme> = [
    new TaskTheme('default', 'greybackground', 'animation', false),
    new TaskTheme('child', 'childBackGround', 'childAnimation', false, 'childAnimationFixed'),
    // Light theme reuses the default ball/animation; only the DOM background differs (handled in React).
    new TaskTheme('light', 'greybackground', 'animation', false),
  ];
  private commandQueue: CommandQueue;
  background?: Phaser.GameObjects.Image;
  font?: string;
  random?: RandomNumberGenerator;
  composite: CompositeTask;
  autoPilot: boolean = false;
  taskAnswers: Answer[] = [];
  targetDifficulty: number = 10;
  updateDifficulty: number = 6000;
  taskAnswersPractitionerSide: number[] = [];
  seed: number = Date.now();
  calledBy: string = '';
  isTablet: boolean = false;
  newAnswer: boolean = false;
  private theme: TaskTheme;
  // Maps from sprite key to sprite (e.g. ball-red)
  private spriteKeyToSprite: Map<string, Sprite> = new Map<string, Sprite>();
  private difficulties: Array<Difficulty>;

  private refreshAutoPilot: boolean = false;

  constructor(name: string) {
    super(name);
    this.random = new RandomNumberGenerator(this.seed);
    this.theme = this.themes[0];
    this.commandQueue = new CommandQueue();
    // Done so we do not need to null check, as after Create() it will be set up.
    this.composite = new CompositeTask(
      new CompositeTaskConfiguration(this.seed, [
        new MovingTargetTaskConfiguration(this.themes[0], this.seed),
        new AudioTaskConfiguration(this.seed),
        new CalculationTaskConfiguration(this.seed),
        new StroopTaskConfiguration(this.seed),
        new ReactionTaskConfiguration(this.seed),
      ]),
      this
    );

    // this.t = this.random.randomAngle();

    let easiest = this.composite.configuration.Copy();

    easiest.GetConfiguration(MovingTargetTaskConfiguration).speedModifier = CONSTANTS.movingTarget.easiestSpeedModifier;

    let hardest = this.composite.configuration.Copy();

    hardest.GetConfiguration(MovingTargetTaskConfiguration).speedModifier = CONSTANTS.movingTarget.hardestSpeedModifier;

    let difficulties = [];

    let difficultySteps = 20;
    for (let i = 0; i < difficultySteps; i++) {
      let perc = i / (difficultySteps - 1); // goes from 0.0 to 1.0
      let createDifficulty = this.composite.configuration.Copy();

      let v = { target: createDifficulty, from: easiest, to: hardest, percentage: perc };

      this.setupValue(ReactionTaskConfiguration, v, 'timeOffsetAnimation');
      this.setupValue(ReactionTaskConfiguration, v, 'timeBetweenAuditoryReaction');
      this.setupValue(ReactionTaskConfiguration, v, 'timeBetweenVisualReaction');
      this.setupValue(CalculationTaskConfiguration, v, 'timeBetweenCalculations');
      this.setupValue(StroopTaskConfiguration, v, 'timeBetweenStroopReaction');
      this.setupValue(MovingTargetTaskConfiguration, v, 'speedModifier');

      let difficultyPercentage = Phaser.Math.Linear(0, 100, perc);
      let difficultyIndex = Phaser.Math.Linear(1, 20, perc);
      difficulties.push(new Difficulty(difficultyPercentage, difficultyIndex, createDifficulty));
    }

    this.difficulties = difficulties;
  }

  setupValue<T extends { [K in P]: number } & ITaskConfiguration, P extends keyof T>(
    type: new (...params: any[]) => T,
    v: {
      target: CompositeTaskConfiguration;
      from: CompositeTaskConfiguration;
      to: CompositeTaskConfiguration;
      percentage: number;
    },
    key: P
  ) {
    let config = v.target.GetConfiguration(type);
    let fromV = v.from.GetConfiguration(type);
    let toV = v.to.GetConfiguration(type);

    (config[key] as any) = Phaser.Math.Linear(fromV[key], toV[key], v.percentage);
  }

  preload() {
    //animation balls
    this.load.spritesheet('animation', 'assets/phaser/images/Silver_16_flat_8192x512_3.png', {
      frameWidth: 512,
      frameHeight: 512,
    });

    // Child theme target: penguin/owl, built from two stacked 16-frame sheets.
    // - "tint" sheet is the body; it receives the ball colour via setTint.
    // - "fixed" sheet is the face (eyes, beak); it keeps its own colours and is drawn on top.
    this.load.spritesheet('childAnimation', 'assets/phaser/images/penguin_owl_tint_8192x512.png', {
      frameWidth: 512,
      frameHeight: 512,
    });

    this.load.spritesheet('childAnimationFixed', 'assets/phaser/images/penguin_owl_fixed_8192x512.png', {
      frameWidth: 512,
      frameHeight: 512,
    });

    // Child theme
    this.load.image('childBackGround', 'assets/phaser/images/child_bg_emptylandscape.png');

    // Reaction images
    this.load.image('NoAnswer', 'assets/tutorial/noclick_100.png');
    this.load.image('OneClick', 'assets/tutorial/click_100.png');
    this.load.image('TwoClicks', 'assets/tutorial/doubleclick_100.png');

    // Sounds
    this.load.audio('clicks', 'assets/phaser/sounds/irregular-clicks-single.mp3');
    this.load.audio('plop', 'assets/phaser/sounds/Plop.mp3');
    this.load.audio('buzzer', 'assets/phaser/sounds/randombuzzer.mp3');

    // Audio reaction
    this.load.audio('auditorySingle', 'assets/phaser/sounds/single_ogg.mp3');
    this.load.audio('auditoryDouble', 'assets/phaser/sounds/double_ogg.mp3');
  }

  keySpace?: Phaser.Input.Keyboard.Key;
  keyDelete?: Phaser.Input.Keyboard.Key;
  keyDown?: Phaser.Input.Keyboard.Key;
  // t: number;

  create() {
    for (const theme of this.themes) {
      // Light shares the default ball sprite key ('animation') — skip so we don't build a duplicate.
      if (this.spriteKeyToSprite.has(theme.spriteKeyPostfix)) continue;
      let sprite = this.add.sprite(
        CONSTANTS.movingTarget.imageX,
        CONSTANTS.movingTarget.imageY,
        theme.spriteKeyPostfix
      );
      sprite.setDepth(100);
      sprite.setVisible(false);
      sprite.setScale(3);
      sprite.setFrame(0);
      sprite.setTint(0xffffff);
      this.spriteKeyToSprite.set(theme.spriteKeyPostfix, sprite);

      // A theme may define an overlay layer (e.g. the child penguin's face) drawn on top of
      // the body. Only the body sprite above is ever tinted; the overlay keeps its own colours.
      if (theme.overlaySpriteKey) {
        let overlay = this.add.sprite(
          CONSTANTS.movingTarget.imageX,
          CONSTANTS.movingTarget.imageY,
          theme.overlaySpriteKey
        );
        overlay.setDepth(101);
        overlay.setVisible(false);
        overlay.setScale(3);
        overlay.setFrame(0);
        overlay.setTint(0xffffff);
        this.spriteKeyToSprite.set(theme.overlaySpriteKey, overlay);
      }
    }

    this.sound.pauseOnBlur = false; // with this setting, client still can hear sounds from the game, when he selected another tab or window.

    // DEBUG set the same sprite for all themes

    this.random = new RandomNumberGenerator(this.seed);

    ////////
    // TASK CREATION
    ////////

    // Loop through difficulties, and in config, set spriteKeyToSprite
    this.difficulties.forEach((difficulty) => {
      difficulty.configuration.GetConfiguration(MovingTargetTaskConfiguration).spriteKeyToSprite =
        this.spriteKeyToSprite;
    });

    let defaultConfiguration = this.difficulties[9].configuration;
    this.composite = new CompositeTask(defaultConfiguration, this);

    // listeners to different input reactions

    this.composite.GetTask(MovingTargetTask).OnColourChangeExpected.subscribe((data) => {
      this.composite.GetTask(AudioTask).CreatePlopSound(this);
    });

    this.composite.GetTask(ReactionTask).OnShapeChangeExpected.subscribe((data) => {
      this.commandQueue.enqueue(sessionId().sessionId, sessionId().treatmentId, data.input);

      let currentTime = new Date().getTime();
      this.taskAnswers.push({ time: currentTime, isAnswerCorrect: data.input });
    });

    this.composite.GetTask(ReactionTask).OnInputReceived.subscribe((data) => {
      this.commandQueue.enqueue(sessionId().sessionId, sessionId().treatmentId, data.input);

      let currentTime = new Date().getTime();
      this.taskAnswers.push({ time: currentTime, isAnswerCorrect: data.input });
    });

    this.composite.GetTask(ReactionTask).RightOrWrongVisualExpected.subscribe((data) => {
      this.commandQueue.enqueue(sessionId().sessionId, sessionId().treatmentId, data.input);

      let currentTime = new Date().getTime();
      this.taskAnswers.push({ time: currentTime, isAnswerCorrect: data.input });
    });

    this.composite.GetTask(CalculationTask).OnCalculationResult.subscribe((data) => {
      this.commandQueue.enqueue(sessionId().sessionId, sessionId().treatmentId, data.input);

      let currentTime = new Date().getTime();
      this.taskAnswers.push({ time: currentTime, isAnswerCorrect: data.input });
    });

    this.composite.GetTask(StroopTask).OnStroopResult.subscribe((data) => {
      this.commandQueue.enqueue(sessionId().sessionId, sessionId().treatmentId, data.input);

      let currentTime = new Date().getTime();
      this.taskAnswers.push({ time: currentTime, isAnswerCorrect: data.input });
    });

    this.composite.Create(defaultConfiguration, this);

    // Keep game objects sized and centered on any canvas resize
    // (container toggle, accordion open/close, or window resize)
    this.scale.on('resize', this.handleScaleResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.scale.off('resize', this.handleScaleResize, this);
    });

    let calculationTaskConfig = new CalculationTaskConfiguration(this.seed);
    calculationTaskConfig.mode = CalculationDifficultyOptions.Off;
    // calculationTaskConfig.font = allFonts;

    let audioConfig = new AudioTaskConfiguration(this.seed);
    audioConfig.enabled = false;
  }

  private handleScaleResize(
    gameSize:
      | Phaser.Structs.Size
      | { width: number; height: number; baseSize?: any; displaySize?: any; resolution?: number }
  ) {
    const width = (gameSize as any).width ?? this.game.canvas.width;
    const height = (gameSize as any).height ?? this.game.canvas.height;

    // Update main camera bounds to the new size
    this.cameras.resize(width, height);

    const movingTarget = this.composite.GetTask(MovingTargetTask);
    if (movingTarget && (movingTarget as any).RebuildAfterResize) {
      (movingTarget as any).RebuildAfterResize(this);
    }

    const visualTask: any = this.composite.GetTask(ReactionTask as any);
    if (visualTask && visualTask.RebuildAfterResize) {
      visualTask.RebuildAfterResize(this);
    }

    const calculationTask: any = this.composite.GetTask(CalculationTask as any);
    if (calculationTask && calculationTask.RebuildAfterResize) {
      calculationTask.RebuildAfterResize(this);
    }

    const stroopTask: any = this.composite.GetTask(StroopTask as any);
    if (stroopTask && stroopTask.RebuildAfterResize) {
      stroopTask.RebuildAfterResize(this);
    }

    this.updateTask(this.composite.configuration);
  }

  public refreshLayout() {
    this.handleScaleResize({ width: this.game.canvas.width, height: this.game.canvas.height });
  }

  startTask() {
    this.composite.Start(this);
  }

  updateTask(config: CompositeTaskConfiguration) {
    this.composite.Update(config, this, false);
    // if (this.calledBy !== 'client') {
    // this.background?.setTexture(config.GetConfiguration(MovingTargetTaskConfiguration).theme.background);
    // }
  }

  updateTaskBasedOnEncodedCommand(encodedCommand: string, encodedTheme?: number, useLegacy?: boolean) {
    let config = this.parse(encodedCommand, encodedTheme, useLegacy);
    this.updateTask(config);
  }

  update(time: number, delta: number) {
    super.update(time, delta);
    this.composite.Render();
    if (!this.autoPilot) {
      // clear autopilot visual and auditory reaction  configurations, when it off
      let refreshDurationConfiguration = this.composite.configuration.Copy();
      let refreshDurationConfigurationTask = refreshDurationConfiguration.GetConfiguration(ReactionTaskConfiguration);
      let refreshSpeedModifierConfiguration =
        refreshDurationConfiguration.GetConfiguration(MovingTargetTaskConfiguration);
      if (
        refreshDurationConfigurationTask.timeToDecreaseAuditoryReaction !==
          CONSTANTS.reaction.timeToDecreaseVisualReaction &&
        refreshDurationConfigurationTask.timeToDecreaseVisualReaction !==
          CONSTANTS.reaction.timeToDecreaseAuditoryReaction &&
        refreshSpeedModifierConfiguration.speedModifier !== CONSTANTS.movingTarget.speedModifier
      ) {
        refreshDurationConfigurationTask.timeToDecreaseVisualReaction = 0;
        refreshDurationConfigurationTask.timeToDecreaseAuditoryReaction = 0;
        refreshSpeedModifierConfiguration.speedModifier = CONSTANTS.movingTarget.defaultSpeedModifier;

        refreshDurationConfiguration.GetConfiguration(StroopTaskConfiguration).mode = false;
        this.composite.Update(refreshDurationConfiguration, this, false);
      }

      return;
    }
    let updateAutopilot = this.newAnswer;
    if (!updateAutopilot) return;
    this.newAnswer = false;
    let allTasks = this.taskAnswersPractitionerSide;

    if (updateAutopilot) {
      if (allTasks.length !== 0 && allTasks.length > 2) {
        this.checkIfWrongOrRightAnswerAutoPilot();
      } else {
        console.log('[AUTO-PILOT] NO ANSWERS');
      }
    }

    if (!this.refreshAutoPilot) {
      return;
    }
  }

  updateAutoPilot(enabled: boolean) {
    this.autoPilot = enabled;

    // Make sure we refresh the autopilot if we enable it.
    if (enabled) this.triggerAutoPilotRefresh(true);
  }

  stopTask() {
    this.composite.Stop(this);
  }

  triggerAutoPilotRefresh(triggerAutoPilotRefresh: boolean) {
    this.refreshAutoPilot = triggerAutoPilotRefresh;
  }

  checkIfWrongOrRightAnswerAutoPilot() {
    let lastTwoAnswersIncorrect = this.checkLastTwo();
    let lastThreeAnswersCorrect = this.checkLastThree();

    if (lastThreeAnswersCorrect) {
      // changeDifficulty = increasingDifficulty;
      ChangeBallSpeed(true);
    } else if (lastTwoAnswersIncorrect) {
      // changeDifficulty = decreasingDifficulty;
      ChangeBallSpeed(false);
    } else {
      return;
    }
  }

  checkLastTwo(): boolean {
    let allTasks = this.taskAnswersPractitionerSide;
    if (allTasks.slice(-2).every((val) => !!val === false)) {
      // this.lastCheckTwo = true;
      return true;
    }

    return false;
  }

  checkLastThree(): boolean {
    let allTasks = this.taskAnswersPractitionerSide;

    if (allTasks.slice(-3).every((val) => !!val === true)) {
      // this.lastCheckThree = true;
      return true;
    }

    return false;
  }

  ClientToPractitionerTaskAnswer(lastAnswer: string) {
    if (this.autoPilot) {
      this.newAnswer = true;
      let parsedLastAnswer = lastAnswer.split(':')[1];
      this.taskAnswersPractitionerSide.push(Number(parsedLastAnswer));
    }

    //Create array of correct and incorrect answers on practitioner side and get answers from command.
    //remove old  difficulty approach that we sending to rpactitioner
    //do logic on the prectitioner side and send it back via backend
  }

  // SUIC:....
  // MCT:0,{index}
  parse(command: string, theme: number | undefined, useLegacy?: boolean): CompositeTaskConfiguration {
    let config = this.composite.configuration.Copy();
    let commanndsSUIC: number[] = [];
    let commanndsMCT: number[] = [];
    let commandTC: number = 0;
    let parsed = command.split(',');
    if (theme !== undefined) {
      // theme is the numeric Theme enum (0 default, 1 child, 2 light). Guard against any
      // out-of-range value so an unknown theme can never crash the scene (falls back to default).
      config.GetConfiguration(MovingTargetTaskConfiguration).theme = this.themes[theme] ?? this.themes[0];
    }
    if (useLegacy) {
      config.GetConfiguration(MovingTargetTaskConfiguration).useLegacyColors = useLegacy;
    }
    // if it is a response command, parse it.
    if (command.startsWith('RPF:')) {
      let splited = [...command.split(':')];

      let lastValue = splited.length - 1;
      let result = splited[lastValue];
      let isCorrect = Number(result) !== 0;

      this.composite.HandleUserResponseFromServer(isCorrect);
    } else if (command.startsWith('SUIC:')) {
      parsed.map((el) => {
        // method to separate name from the commands

        if (el.startsWith('SUIC:')) {
          let separateString = [...el];
          let value = separateString.length - 1;
          let result = separateString[value];
          commanndsSUIC.push(Number(result));
        } else {
          commanndsSUIC.push(Number(el));
        }
      });
      // Parse the general variables double !! returns boolean

      let isStarted = !commanndsSUIC[0];
      let eyeMovement = !!commanndsSUIC[1];
      let speed = commanndsSUIC[2];
      let trajectory = commanndsSUIC[3];
      let interactive = !!commanndsSUIC[4];
      let autoPilot = !!commanndsSUIC[5];
      let reactionVisual = !!commanndsSUIC[6];
      let reactionAudio = !!commanndsSUIC[7];
      let isColourManual = commanndsSUIC[8];
      let calculationDifficulty = commanndsSUIC[9];
      let sound = commanndsSUIC[10];
      let clicks = !!commanndsSUIC[11];
      let plops = !!commanndsSUIC[12];
      let buzzer = commanndsSUIC[13];
      let animation = commanndsSUIC.length > 15 ? !!commanndsSUIC[14] : false;
      let stroop = commanndsSUIC.length > 15 ? !!commanndsSUIC[15] : false;
      let seed = commanndsSUIC.length > 15 ? commanndsSUIC[16] : commanndsSUIC[14];
      this.seed = seed;

      config.GetConfiguration(StroopTaskConfiguration).mode = stroop;
      config.GetConfiguration(CalculationTaskConfiguration).mode = calculationDifficulty;
      config.GetConfiguration(MovingTargetTaskConfiguration).SphereTrajectoryOption = trajectory;
      config.GetConfiguration(MovingTargetTaskConfiguration).speedModifier = speed * CONSTANTS.speedChanger;
      config.GetConfiguration(MovingTargetTaskConfiguration).isAutomatically = isColourManual;
      config.GetConfiguration(ReactionTaskConfiguration).animation = animation;
      config.seed = seed;
      // visualreactionVisual
      config.GetConfiguration(ReactionTaskConfiguration).visual = reactionVisual;
      config.GetConfiguration(ReactionTaskConfiguration).auditory = reactionAudio;
      config.GetConfiguration(AudioTaskConfiguration).click = clicks;
      config.GetConfiguration(AudioTaskConfiguration).plop = plops;
      config.GetConfiguration(AudioTaskConfiguration).zoomer = buzzer;

      if (eyeMovement) {
        config.GetConfiguration(MovingTargetTaskConfiguration).enabled = true;
      } else {
        config.GetConfiguration(MovingTargetTaskConfiguration).enabled = false;
      }

      if (!autoPilot) {
        config.GetConfiguration(ReactionTaskConfiguration).timeOffsetAnimation = CONSTANTS.reaction.timeOffsetAnimation;
        config.GetConfiguration(ReactionTaskConfiguration).timeBetweenAuditoryReaction =
          CONSTANTS.reaction.timeBetweenAuditoryReaction;
        config.GetConfiguration(ReactionTaskConfiguration).timeBetweenVisualReaction =
          CONSTANTS.reaction.timeBetweenVisualReaction;
        config.GetConfiguration(CalculationTaskConfiguration).timeBetweenCalculations =
          CONSTANTS.calculation.timeBetweenCalculations;
        config.GetConfiguration(StroopTaskConfiguration).timeBetweenStroopReaction =
          CONSTANTS.stroop.timeBetweenStroopReaction;
      }

      if (interactive) {
        // console.log('calculation mode: ' ,config.GetConfiguration(CalculationTaskConfiguration).mode)
        if (reactionVisual || reactionAudio || animation) {
          config.GetConfiguration(ReactionTaskConfiguration).enabled = true;
        } else {
          config.GetConfiguration(ReactionTaskConfiguration).enabled = false;
        }

        if (animation) {
          config.GetConfiguration(ReactionTaskConfiguration).animation = true;
        }

        if (stroop) {
          config.GetConfiguration(StroopTaskConfiguration).mode = true;
          config.GetConfiguration(StroopTaskConfiguration).enabled = true;
        } else {
          config.GetConfiguration(StroopTaskConfiguration).mode = false;
          config.GetConfiguration(StroopTaskConfiguration).enabled = false;
        }
        if (config.GetConfiguration(CalculationTaskConfiguration).mode !== 0) {
          config.GetConfiguration(CalculationTaskConfiguration).enabled = true;
        }
        this.updateAutoPilot(autoPilot);
      } else {
        config.GetConfiguration(ReactionTaskConfiguration).enabled = false;
        config.GetConfiguration(CalculationTaskConfiguration).enabled = false;
        config.GetConfiguration(ReactionTaskConfiguration).animation = false;
        config.GetConfiguration(StroopTaskConfiguration).mode = false;
      }

      if (sound) {
        if (clicks || plops || buzzer !== 0) {
          config.GetConfiguration(AudioTaskConfiguration).enabled = true;
        }
      } else {
        config.GetConfiguration(AudioTaskConfiguration).enabled = false;
        config.GetConfiguration(AudioTaskConfiguration).zoomer = Zoomer.off;
        config.GetConfiguration(AudioTaskConfiguration).click = false;
      }
    } else if (command.startsWith('MCT:')) {
      const payload = command.slice(4).trim();
      const rgbMatch = payload.match(/^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)$/);
      const noLegacy = payload.includes('rgb');

      if (noLegacy) {
        const [, rStr, gStr, bStr] = rgbMatch;
        const r = Number(rStr),
          g = Number(gStr),
          b = Number(bStr);
        const colourNum = (r << 16) | (g << 8) | b;
        config.GetConfiguration(MovingTargetTaskConfiguration).colour = colourNum;
        config.GetConfiguration(MovingTargetTaskConfiguration).useLegacyColors = false;
      } else {
        const nums = payload.split(',').map((s) => Number(s));
        const legacyIndex = nums[1];
        config.GetConfiguration(MovingTargetTaskConfiguration).colour = legacyIndex;
        config.GetConfiguration(MovingTargetTaskConfiguration).useLegacyColors = true;
      }
    }
    if (command.startsWith('TC:')) {
      parsed.map((el) => {
        if (el.startsWith('TC:')) {
          let separateString = [...el];
          let value = separateString.length - 1;
          let result = separateString[value];
          commandTC = Number(result);
        } else {
          commandTC = Number(el);
        }
      });

      config.GetConfiguration(MovingTargetTaskConfiguration).theme = this.themes[commandTC] ?? this.themes[0];
    }
    return config;
  }
}
